import type { GroupedOptionsType } from '@atlaskit/select/types';

import type { CustomOptionType } from './LabelsSelect.tsx';

/**
 * Pure helpers for merging admin-managed (external) labels into the shared
 * LabelsSelect picker. React/react-select-free so grouping, filtering, and
 * selected-value logic can be unit-tested directly.
 */

// Applied managed label shape (from the Relay `useExternalLabels` read).
export type AppliedManagedLabel = {
	readonly id?: string | null;
	readonly name?: string | null;
	readonly kind?: string | null;
};

// Available managed label shape (from the org REST endpoint via
// `useAvailableManagedLabels`).
export type AvailableManagedLabel = {
	readonly id: string;
	readonly name: string;
	readonly status?: string;
};

// `isNoMatch` marks the non-selectable "No matches." row under an empty group.
export type ManagedAwareOption = CustomOptionType & {
	isNoMatch?: boolean;
};

// Managed labels are keyed by a page entity ARI, so only pages and blogs are eligible.
export const isManagedEligibleContentType = (type: string | null | undefined): boolean =>
	type === 'page' || type === 'blogpost';

/** Converts an applied managed label into a picker option (a selected tag). */
export const transformAppliedManagedToOption = (
	label: AppliedManagedLabel,
): ManagedAwareOption | null => {
	const id = label.id ?? '';
	const name = label.name ?? '';
	if (!id || !name) {
		return null;
	}
	return {
		id,
		label: name,
		value: name,
		name,
		prefix: '',
		strategy: 'managed',
		labelKind: 'managed',
		managedLabelId: id,
	};
};

/** Converts the applied managed labels list into selected picker options. */
export const transformAppliedManagedToOptions = (
	appliedManaged: readonly AppliedManagedLabel[],
): ManagedAwareOption[] =>
	appliedManaged
		.map(transformAppliedManagedToOption)
		.filter((option): option is ManagedAwareOption => option != null);

/**
 * Builds the selectable "Managed labels" group: available org managed labels,
 * excluding already-applied ones, filtered by the typed input (client-side).
 * Only ACTIVE (or status-less) labels are offered.
 */
export const buildAvailableManagedOptions = ({
	availableManaged,
	appliedManaged,
	inputValue,
}: {
	availableManaged: readonly AvailableManagedLabel[];
	appliedManaged: readonly AppliedManagedLabel[];
	inputValue: string;
}): ManagedAwareOption[] => {
	const appliedNames = new Set(
		appliedManaged.map((l) => (l.name ?? '').toLowerCase()).filter(Boolean),
	);
	const normalizedInput = inputValue.trim().toLowerCase();

	// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- Set.has is O(1); chained filters are O(n), not O(n*m). Managed label lists are small (org-scoped).
	return availableManaged
		.filter((l) => l.status == null || l.status === 'ACTIVE')
		.filter((l) => !appliedNames.has(l.name.toLowerCase()))
		.filter((l) => (normalizedInput ? l.name.toLowerCase().includes(normalizedInput) : true))
		.map((l) => ({
			id: l.id,
			label: l.name,
			value: l.name,
			name: l.name,
			prefix: '',
			strategy: 'managed',
			labelKind: 'managed' as const,
			managedLabelId: l.id,
		}));
};

/** A disabled "No matches." placeholder row for an otherwise-empty group. */
const makeNoMatchOption = (groupKey: string): ManagedAwareOption => ({
	id: `__no_match_${groupKey}__`,
	label: '',
	value: `__no_match_${groupKey}__`,
	name: '',
	prefix: '',
	strategy: 'no_match',
	isNoMatch: true,
});

/** True when an option is a non-selectable placeholder. */
export const isNonSelectableOption = (
	option: (CustomOptionType & { isNoMatch?: boolean }) | null | undefined,
): boolean => Boolean(option?.isNoMatch);

/**
 * Groups the picker options into "Managed labels" then "User labels" when the org
 * has managed labels and the content is eligible; empty groups get a "No matches."
 * placeholder so the heading still renders. Otherwise returns the user options
 * untouched, preserving existing behavior.
 */
export const buildGroupedLabelOptions = ({
	managedEnabled,
	orgHasManagedLabels,
	managedGroupOptions,
	userOptions,
	managedGroupHeading,
	userGroupHeading,
}: {
	managedEnabled: boolean;
	orgHasManagedLabels: boolean;
	managedGroupOptions: ManagedAwareOption[];
	// The existing options structure: either a flat option list (search results)
	// or an already-grouped recommended list.
	userOptions: GroupedOptionsType<CustomOptionType> | CustomOptionType[];
	managedGroupHeading: string;
	userGroupHeading: string;
}): GroupedOptionsType<CustomOptionType> => {
	// Flatten the existing user options (possibly already a "Suggested labels" group).
	const flatUserOptions: CustomOptionType[] = Array.isArray(userOptions)
		? userOptions.flatMap((entry) =>
				entry && 'options' in entry ? (entry.options ?? []) : [entry as CustomOptionType],
			)
		: [];

	if (!managedEnabled || !orgHasManagedLabels) {
		// Preserve existing behavior exactly.
		return (
			Array.isArray(userOptions) && userOptions.every((o) => o && 'options' in o)
				? userOptions
				: [{ label: userGroupHeading, options: flatUserOptions }]
		) as GroupedOptionsType<CustomOptionType>;
	}

	return [
		{
			label: managedGroupHeading,
			options: managedGroupOptions.length ? managedGroupOptions : [makeNoMatchOption('managed')],
		},
		{
			label: userGroupHeading,
			options: flatUserOptions.length ? flatUserOptions : [makeNoMatchOption('user')],
		},
	];
};
